import { useMapSyncerClient } from '@/api-proxy/useProxiedClient'
import { getRemoteSyncOptions, startRemoteSyncMutation, stopRemoteSyncMutation } from '@/api/mapSyncApi'
import type { DataSyncStatus } from '@/api/mapSyncApi/types.gen'
import { formatDateTime } from '@/utils/datetime'
import { extendKey } from '@/utils/queryKey'
import { Button, Group, Stack, Switch, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FaSync } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

const SYNC_STATUS_KEYS: Record<NonNullable<DataSyncStatus>, string> = {
  Waiting: 'dataSyncWaiting',
  InProgress: 'dataSyncInProgress',
  Finished: 'dataSyncFinished',
}

const isNotImplemented = (error: unknown) =>
  !!error && typeof error === 'object' && 'status' in error && error.status === 'not_implemented'

export const VehicleRemoteSyncPanel = ({ vehicleId }: { vehicleId: string | undefined }) => {
  const { t } = useTranslation('vehicles')
  const client = useMapSyncerClient(vehicleId)

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    ...extendKey(getRemoteSyncOptions({ client }), vehicleId),
    enabled: !!vehicleId && !!client,
    retry: false,
    staleTime: 2000,
  })

  const notImplemented = isNotImplemented(error)

  const { mutate: startRemoteSync, isPending: isStarting } = useMutation({
    ...startRemoteSyncMutation({ client }),
    onSuccess: () => notifications.show({ message: t('remoteSyncStarted') }),
    onError: () => notifications.show({ message: t('errorStartingRemoteSync'), color: 'red' }),
    onSettled: () => {
      refetch()
    },
  })

  const { mutate: stopRemoteSync, isPending: isStopping } = useMutation({
    ...stopRemoteSyncMutation({ client }),
    onSuccess: () => notifications.show({ message: t('remoteSyncStopped') }),
    onError: () => notifications.show({ message: t('errorStoppingRemoteSync'), color: 'red' }),
    onSettled: () => {
      refetch()
    },
  })

  const active = !!data?.active
  const syncStatus = data?.sync_status

  return (
    <Stack>
      <Group justify="space-between">
        <Switch
          size="sm"
          label={t('remoteNasSync')}
          checked={active}
          disabled={!vehicleId || isLoading || notImplemented || isStarting || isStopping}
          onChange={event => (event.currentTarget.checked ? startRemoteSync({}) : stopRemoteSync({}))}
        />
        <Button
          variant="light"
          size="xs"
          leftSection={<FaSync size={12} />}
          onClick={() => refetch()}
          loading={isFetching}
          disabled={!vehicleId}
        >
          {t('refresh')}
        </Button>
      </Group>

      {notImplemented && (
        <Text size="sm" c="dimmed">
          {t('remoteSyncNotAvailable')}
        </Text>
      )}

      {active && (
        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" fw={500}>
              {t('remoteSyncIp')}
            </Text>
            <Text size="sm">{data?.ip || t('remoteSyncUnknown')}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={500}>
              {t('remoteSyncStatus')}
            </Text>
            <Text size="sm">{syncStatus ? t(SYNC_STATUS_KEYS[syncStatus]) : t('remoteSyncUnknown')}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={500}>
              {t('remoteSyncExpiresAt')}
            </Text>
            <Text size="sm">{data?.expires_at ? formatDateTime(data.expires_at) : t('remoteSyncUnknown')}</Text>
          </Group>
        </Stack>
      )}
    </Stack>
  )
}
